import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowLeft, X } from "lucide-react";
import { PORTFOLIO_PROJECTS } from "../mock";
import { Dialog, DialogContent } from "../components/ui/dialog";
import Seo from "../components/Seo";

const PortfolioProject = () => {
  const { slug } = useParams();
  const project = PORTFOLIO_PROJECTS.find((p) => p.slug === slug);
  const [active, setActive] = useState(null);

  if (!project) return <Navigate to="/portfolio" replace />;

  // Sibling navigation
  const idx = PORTFOLIO_PROJECTS.findIndex((p) => p.slug === slug);
  const next = PORTFOLIO_PROJECTS[(idx + 1) % PORTFOLIO_PROJECTS.length];

  return (
    <div className="bg-[#0E0E0F] text-[#EDE3CB]">
      <Seo
        path={`/portfolio/${project.slug}`}
        title={project.title}
        type="article"
        image={project.poster || project.hero}
        description={`${project.title} (${project.studio}, ${project.year}) — ${project.subtitle}. Property work by Jesse J. Adams · ${project.role}.`}
      />
      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={project.hero} alt={project.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0E0E0F]/55 via-[#0E0E0F]/45 to-[#0E0E0F]" />
        </div>
        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-12 py-16 w-full">
          <Link to="/portfolio" className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-[#EDE3CB]/75 hover:text-[#B8935A] transition-colors mb-8">
            <ArrowLeft size={14} /> Back to Portfolio
          </Link>
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#B8935A] mb-3">
            {project.studio} · {project.year}
          </div>
          <h1 className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.9]">{project.title}</h1>
          <p className="font-serif-accent italic text-xl md:text-2xl text-[#EDE3CB]/85 max-w-2xl mt-5 leading-snug">
            {project.subtitle}
          </p>
        </div>
      </section>

      {/* Meta strip */}
      <section className="tickerstrip">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12 py-6 grid grid-cols-2 md:grid-cols-3 gap-6">
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#EDE3CB]/55">Director</div>
            <div className="font-display text-xl mt-1">{project.director}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#EDE3CB]/55">Role</div>
            <div className="font-display text-xl mt-1">{project.role}</div>
          </div>
          <div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#EDE3CB]/55">Studio · Year</div>
            <div className="font-display text-xl mt-1">{project.studio} · {project.year}</div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-3">
          <div className="section-label lg:sticky lg:top-24">Case Study</div>
        </div>
        <div className="lg:col-span-9 space-y-6 text-[17px] leading-[1.8] text-[#EDE3CB]/85">
          {project.body.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      {/* Gallery */}
      {project.images && project.images.length > 1 && (
        <section className="max-w-[1280px] mx-auto px-6 lg:px-12 pb-20">
          <div className="section-label mb-6">Gallery</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {project.images.map((src, i) => (
              <button
                key={i}
                onClick={() => setActive(src)}
                className="group relative overflow-hidden bg-[#0B0B0C] border border-white/5 aspect-[16/10]"
              >
                <img src={src} alt={`${project.title} — ${i + 1}`} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]" />
                <div className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#B8935A] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <ArrowUpRight size={16} className="text-[#0E0E0F]" />
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Next + CTA */}
      <section className="max-w-[1280px] mx-auto px-6 lg:px-12 pb-24">
        <div className="border-t border-white/10 pt-10 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <div>
            <div className="section-label">Next project</div>
            <Link to={`/portfolio/${next.slug}`} className="font-display text-4xl md:text-5xl mt-3 leading-tight inline-flex items-center gap-3 hover:text-[#B8935A] transition-colors">
              {next.title} <ArrowUpRight size={28} className="text-[#B8935A]" />
            </Link>
          </div>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="btn-primary">Check Availability <ArrowUpRight size={14} /></Link>
            <Link to="/portfolio" className="btn-secondary">All Case Studies</Link>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-5xl bg-[#0B0B0C] border-[#B8935A]/30 text-[#EDE3CB] p-0 overflow-hidden">
          {active && (
            <div className="relative">
              <img src={active} alt="" className="w-full max-h-[80vh] object-contain bg-[#0B0B0C]" />
              <button onClick={() => setActive(null)} className="absolute top-3 right-3 w-9 h-9 rounded-full bg-[#0E0E0F]/85 border border-[#B8935A]/40 flex items-center justify-center hover:bg-[#B8935A] hover:text-[#0E0E0F] transition-colors" aria-label="Close">
                <X size={16} />
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PortfolioProject;
